import React, { useEffect, useState } from "react";
import {
    Modal,
    ModalHeader,
    ModalBody,
    ModalFooter,
    Button,
    Badge,
    Spinner,
    Alert,
    Card,
    CardBody,
    Row,
    Col
} from "reactstrap";
import { get } from "helpers/api_helper";
import moment from "moment";

const AuditTrailModal = ({ isOpen, toggle, documentType, documentId, documentNumber }) => {
    const [logs, setLogs] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    useEffect(() => {
        if (isOpen && documentType && documentId) {
            fetchLogs();
        }
    }, [isOpen, documentType, documentId]);

    const fetchLogs = async () => {
        setLoading(true);
        setError(null);
        try {
            const res = await get(`/api/approvals/audit_trail?document_type=${documentType}&document_id=${documentId}`);
            if (res.status === 200) {
                setLogs(res.response || []);
            } else {
                setError(res.message || "Failed to load audit trail.");
            }
        } catch (err) {
            setError(err?.message || "Failed to load audit trail.");
        } finally {
            setLoading(false);
        }
    };

    const parseJSON = (str) => {
        try {
            return JSON.parse(str);
        } catch (e) {
            return str;
        }
    };

    return (
        <Modal isOpen={isOpen} toggle={toggle} size="lg" centered scrollable zIndex="99999">
            <ModalHeader toggle={toggle}>
                <i className="mdi mdi-history text-primary me-2"></i>
                Audit Trail: {documentType?.toUpperCase()} #{documentNumber || documentId}
            </ModalHeader>
            <ModalBody>
                {error && <Alert color="danger">{error}</Alert>}

                {loading ? (
                    <div className="text-center py-4">
                        <Spinner color="primary" />
                        <div className="mt-2 text-muted">Loading audit records...</div>
                    </div>
                ) : logs.length === 0 ? (
                    <div className="text-center py-4 text-muted">
                        No edit history or approval records found for this document.
                    </div>
                ) : (
                    <div className="timeline">
                        {logs.map((log, index) => {
                            const prev = parseJSON(log.previous_values);
                            const updated = parseJSON(log.updated_values);

                            return (
                                <Card key={log.id || index} className="border mb-3 shadow-none">
                                    <CardBody className="p-3">
                                        <div className="d-flex justify-content-between align-items-start mb-2">
                                            <div>
                                                <span
                                                    style={{
                                                        background: log.status === "approved" ? "#dcfce7" : log.status === "used" ? "#e0f2fe" : log.status === "rejected" ? "#fee2e2" : "#fef3c7",
                                                        color: log.status === "approved" ? "#166534" : log.status === "used" ? "#0369a1" : log.status === "rejected" ? "#991b1b" : "#92400e",
                                                        border: `1px solid ${log.status === "approved" ? "#86efac" : log.status === "used" ? "#bae6fd" : log.status === "rejected" ? "#fca5a5" : "#fcd34d"}`,
                                                        padding: "3px 10px",
                                                        borderRadius: "20px",
                                                        fontSize: "11px",
                                                        fontWeight: "700",
                                                        marginRight: "8px",
                                                        display: "inline-block"
                                                    }}
                                                >
                                                    {log.status?.toUpperCase()}
                                                </span>
                                                <strong style={{ color: "#0f172a", fontSize: "13px" }}>
                                                    Requested by {log.requested_by_name || log.requested_by}
                                                </strong>
                                            </div>
                                            <span style={{ color: "#475569", fontSize: "12px", fontWeight: "600" }}>
                                                {moment(log.requested_at).format("DD MMM YYYY, hh:mm A")}
                                            </span>
                                        </div>

                                        <div className="p-2 rounded mb-2" style={{ background: "#f8fafc", border: "1px solid #e2e8f0", fontSize: "13px", color: "#0f172a" }}>
                                            <strong>Reason:</strong> {log.reason}
                                        </div>

                                        {log.approved_by_name && (
                                            <div className="small text-muted mb-2">
                                                <i className="mdi mdi-account-check text-success me-1"></i>
                                                <strong>Approved by:</strong> {log.approved_by_name} on{" "}
                                                {moment(log.approved_at).format("DD MMM YYYY, hh:mm A")}
                                            </div>
                                        )}

                                        {log.rejection_reason && (
                                            <div className="small text-danger mb-2">
                                                <i className="mdi mdi-alert-circle me-1"></i>
                                                <strong>Rejection Reason:</strong> {log.rejection_reason}
                                            </div>
                                        )}

                                        {log.completed_at && (
                                            <div className="small text-info mb-2">
                                                <i className="mdi mdi-check-all me-1"></i>
                                                <strong>Edit Executed & Saved:</strong>{" "}
                                                {moment(log.completed_at).format("DD MMM YYYY, hh:mm A")}
                                            </div>
                                        )}

                                        {(prev || updated) && (
                                            <Row className="mt-2 pt-2 border-top g-2" style={{ fontSize: "12px" }}>
                                                {prev && (
                                                    <Col md={updated ? 6 : 12}>
                                                        <span className="fw-bold text-secondary d-block mb-1">
                                                            Previous Values Snapshot:
                                                        </span>
                                                        <pre
                                                            className="bg-light p-2 rounded border mb-0"
                                                            style={{ maxHeight: "150px", overflowY: "auto" }}
                                                        >
                                                            {typeof prev === "object"
                                                                ? JSON.stringify(prev, null, 2)
                                                                : prev}
                                                        </pre>
                                                    </Col>
                                                )}
                                                {updated && (
                                                    <Col md={6}>
                                                        <span className="fw-bold text-primary d-block mb-1">
                                                            Updated Values Saved:
                                                        </span>
                                                        <pre
                                                            className="bg-light p-2 rounded border mb-0"
                                                            style={{ maxHeight: "150px", overflowY: "auto" }}
                                                        >
                                                            {typeof updated === "object"
                                                                ? JSON.stringify(updated, null, 2)
                                                                : updated}
                                                        </pre>
                                                    </Col>
                                                )}
                                            </Row>
                                        )}
                                    </CardBody>
                                </Card>
                            );
                        })}
                    </div>
                )}
            </ModalBody>
            <ModalFooter>
                <Button color="secondary" onClick={toggle}>
                    Close
                </Button>
            </ModalFooter>
        </Modal>
    );
};

export default AuditTrailModal;
